<template>
  <div>
    <transition-group name="formfields" tag="div">
    <div class="field formfields-item"
         v-for="(bit, i) in schema.fields.filter(x => inEditView(x) !== false)"
         :key="bit.field || bit.label">

      <div v-show="bit.view === undefined || bit.view(resourceData, user) || bit.inEditView === true">

        <!-- CASE Object -->

        <template v-if="bit.type == 'prefetchedObject'">
          <fob-label
            :key="`${i}a`"
            :label="bit.label"
            :asterisk="hasAsterisk(bit.field)"
          ></fob-label>
          <fob-entity-select-prefetched
            v-model="resourceData[bit.field]"
            :entity="bit.entity"
            searchMethod="findAll"
            :icon="bit.icon"
            :groupBy="bit.groupBy"
            :orderBy="bit.orderBy"
            :reorder="bit.reorder"
            :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''"
            :preselect="bit.preselect"
            :prefilter="bit.prefilter"
          >
          </fob-entity-select-prefetched>
        </template>

        <template v-else-if="bit.type == 'object'">
          <template v-if="bit.cardinality == 'single'">
            <fob-entity-select
              :ref="bit.field"
              :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''"
              @input="touchField(bit.field)"
              @custom="(val) => $emit('custom', val)"
              v-model="resourceData[bit.field]"
              :key="i"
              :allowCreate="bit.allowCreate || false"
              :label="bit.label"
              :extraButton="bit.extraButton"
              :asterisk="hasAsterisk(bit.field)"
              :icon="bit.icon"
              :method="bit.finder"
              :route="bit.schema.className"
              :schema="bit.schema"
            ></fob-entity-select>
          </template>
          <template v-if="bit.cardinality === 'many'">
            <div :key="i" style="margin-bottom: 20px">
              <template v-if="!bit.onlyCreate">
                <template v-if="bit.widget === 'tableSelectVertical'">
                  <fob-entity-multi-select-vertical
                    :asterisk="hasAsterisk(bit.field)"
                    :schema="bit"
                    :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''"
                    v-model="resourceData[bit.field]"
                  ></fob-entity-multi-select-vertical>
                </template>
                <template v-else>
                  <fob-entity-multi-select
                    :asterisk="hasAsterisk(bit.field)"
                    :schema="bit"
                    :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''"
                    v-model="resourceData[bit.field]"
                  ></fob-entity-multi-select>
                </template>
              </template>
              <template v-else-if="bit.render === 'projektleiter'">
                <fob-projektleiter
                  label="Projektleitung"
                  v-model="resourceData[bit.field]"
                  :asterisk="true"
                  :validation="$v.resourceData[bit.field]"
                  :showErrors="showErrors"
                ></fob-projektleiter>
              </template>
              <template v-else>
                <fob-label
                  :label="bit.label"
                  :asterisk="hasAsterisk(bit.field)"
                >
                </fob-label>
                <div class="box">
                  <transition-group name="list" tag="div">
                    <div v-for="(embedded, j) in resourceData[bit.field]"
                         :key="embedded._id"
                         class="box"
                         style="margin-bottom: 20px"
                    >
                      <button
                        @click="removeEmbedded(bit.field, j)"
                        class="button pull-right is-small is-white has-text-carnelian"
                      >
                        <b-tooltip square class="is-carnelian" position="is-top"
                          :label="$gettext(`embedded.button.remove.${bit.field}`)"
                        >
                          <b-icon icon="trash"></b-icon>
                        </b-tooltip>
                      </button>
                      <fob-entity-create-form
                        v-model="resourceData[bit.field][j]"
                        :route="bit.schema.route"
                        :icon="bit.icon"
                        :schema="bit.schema"
                        :showErrors="showErrors"
                      ></fob-entity-create-form>
                    </div>
                  </transition-group>
                  <button class="button" @click="pushToEmbedded(bit.field, bit.schema.entityName)">
                    <translate>{{ `embedded.button.add.${bit.field}` }}</translate>
                  </button>
                </div>
              </template>
            </div>
          </template>
        </template>

        <!-- CASE Date -->

        <template v-else-if="bit.type === Date">
          <fob-label
            :key="`${i}a`"
            :label="bit.label"
            :asterisk="hasAsterisk(bit.field)"
          ></fob-label>
          <b-field :key="i" :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''">
              <b-datepicker
                  placeholder="Type or select a date..."
                  v-model="resourceData[bit.field]"
                  icon="calendar"
                  :readonly="false"
                  :max-date="maxDate"
              >
              </b-datepicker>
          </b-field>
          <template v-if="(showErrors && isInvalid(bit.field))">
            <translate
              v-if="$v.resourceData[bit.field].isAtLeastFrom === false"
              class="help is-danger">EntityCreateForm.invalid.date.isAtLeastFrom</translate>
          </template>
        </template>

        <!-- CASE Url -->

        <template v-else-if="bit.type === 'link'">
          <fob-label
            :key="`${i}a`"
            :label="bit.label"
            :asterisk="hasAsterisk(bit.field)"
          ></fob-label>
          <b-field :key="i" :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''">
            <b-input placeholder=""
                  type="text"
                  v-model="resourceData[bit.field]"
                  :icon="bit.icon ? bit.icon : ''">
            </b-input>
          </b-field>
        </template>

        <!-- CASE Number -->

        <template v-else-if="bit.type === Number">
          <fob-label
            :key="`${i}a`"
            :label="bit.label"
            :asterisk="hasAsterisk(bit.field)"
          ></fob-label>
          <b-field grouped :key="i" :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''">
            <b-input placeholder="Number"
                  type="number"
                  v-model="resourceData[bit.field]"
                  :icon="bit.icon ? bit.icon : ''"
                  :min="bit.min"
                  :max="bit.max">
            </b-input>
            <b-tooltip square position="is-top" :label="$gettext(`form.xbutton`)">
              <button tabindex="-1" class="button contol is-aquamarine" @click="resourceData[bit.field] = null">
                <b-icon icon="times" size="is-small"></b-icon>
              </button>
            </b-tooltip>
          </b-field>
        </template>

        <!-- CASE Boolean -->

        <template v-else-if="bit.type === Boolean">
          <div v-if="bit.narrow" class="field" style="margin-top: 1rem; margin-left: 1rem" :key="i">
            <b-checkbox v-model="resourceData[bit.field]">
              <span><translate> {{ bit.label }} </translate></span>
            </b-checkbox>
          </div>
          <div v-else class="field" style="margin-top: 2.5rem" :key="i">
            <b-checkbox v-model="resourceData[bit.field]">
              <span><translate> {{ bit.label }} </translate></span>
            </b-checkbox>
          </div>
        </template>

        <!-- CASE Select/Dropdown -->

        <template v-else-if="bit.type === 'select'">
          <fob-label
            :key="`${i}a`"
            :asterisk="hasAsterisk(bit.field)"
            :label="bit.label"
          >
            <b-icon v-if="showErrors && isInvalid(bit.field)" icon="exclamation-circle" type="is-danger"></b-icon>
          </fob-label>
          <b-field grouped :key="i" :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''">
            <b-select v-model="resourceData[bit.field]">
              <option
                v-for="option in bit.values"
                :value="option.value"
                :key="option.value">
                <span v-if="bit.translatable">
                  <translate> {{ option.label }} </translate>
                </span>
                <span v-else>
                  {{ option.label }}
                </span>
              </option>
            </b-select>
            <b-tooltip v-if="bit.nullable !== false" square position="is-top" :label="$gettext(`form.xbutton`)">
              <button tabindex="-1" class="button contol is-aquamarine" @click="resourceData[bit.field] = null">
                <b-icon icon="times" size="is-small"></b-icon>
              </button>
            </b-tooltip>
          </b-field>
        </template>

        <!-- CASE Label, no real field -->

        <template v-else-if="bit.type === 'label'">
          <fob-label
            :label="bit.label"
            :asterisk="hasAsterisk(bit.field)"
            :key="i"
          ></fob-label>
        </template>

        <!-- CASE HTML Textfield with Editor -->

        <template v-else-if="bit.type === 'html-text'">
          <fob-label
            :key="`${i}a`"
            :asterisk="hasAsterisk(bit.field)"
            :label="bit.label"
          ></fob-label>
          <fob-html-editor
            :ref="bit.field"
            :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''"
            :maxLength="bit.maxLength"
            v-model="resourceData[bit.field]"
            @input="touchField(bit.field)"
          ></fob-html-editor>
        </template>

        <!-- CASE Textfields and Strings except computed values -->

        <template v-else-if="!bit.computed">
          <transition name="delay">
            <div v-show="bit.view === undefined || bit.view(resourceData)">
              <fob-label
                :key="`${i}a`"
                :asterisk="hasAsterisk(bit.field)"
                :label="bit.label"
              ></fob-label>
              <b-field grouped :key="i" :type="(showErrors && isInvalid(bit.field)) ? 'is-danger' : ''">
                <b-input
                  v-if="bit.type === 'text'"
                  v-model="resourceData[bit.field]"
                  expanded
                  type="textarea"
                  :maxlength="bit.maxlength"
                  value="" placeholder=""
                ></b-input>
                <template v-else>
                  <b-input
                    @input="touchField(bit.field)"
                    v-model="resourceData[bit.field]"
                    expanded
                    :maxlength="bit.maxlength || 255"
                    :has-counter="false"
                    icon="pencil"
                    value=""
                    placeholder=""
                  ></b-input>
                </template>
                <b-tooltip square position="is-top" :label="$gettext(`form.xbutton`)">
                  <button tabindex="-1" class="button contol is-aquamarine" @click="resourceData[bit.field] = null">
                    <b-icon icon="times" size="is-small"></b-icon>
                  </button>
                </b-tooltip>
              </b-field>
            </div>
          </transition>
        </template>

      </div>
    </div>
    </transition-group>
  </div>
</template>

<script>
import EntitySelect from '@/components/form/EntitySelect'
import EntitySelectPrefetched from '@/components/form/EntitySelectPrefetched'
import EntityMultiSelect from '@/components/form/EntityMultiSelect'
import EntityMultiSelectVertical from '@/components/form/EntityMultiSelectVertical'
import Projektleiter from '@/components/form/Projektleiter'
import Label from '@/components/form/Label'
import Validation from '@/validation'
import draggable from 'vuedraggable'
import { EntityProvider } from 'fobclient/lib/entity/entityProvider'
import HTMLEditor from '@/components/form/HTMLEditor'

export default {
  name: 'fob-entity-create-form',
  data () {
    const today = new Date()

    return {
      modalState: {},
      resourceData: this.value,
      maxDate: new Date(today.getFullYear() + 10, 11, 31)
    }
  },
  validations () {
    if (this.resourceData.skipValidation === true) {
      return {
        resourceData: {}
      }
    }
    if (this.schema.className in Validation) {
      if (['publikation', 'preis'].includes(this.schema.className)) {
        return {
          resourceData: Validation[this.schema.className]()
        }
      } else if (['forschungsprojekt'].includes(this.schema.className)) {
        return {
          resourceData:
            Validation[this.schema.className](
              this.resourceData.projektBeginn,
              this.resourceData.projektEnde,
            )
        }
      } else {
        return {
          resourceData: Validation[this.schema.className]
        }
      }
    } else {
      return {
        resourceData: {}
      }
    }
  },
  props: {
    schema: Object,
    showErrors: {
      type: Boolean,
      default: false
    },
    value: {
      type: Object,
      default () {
        return {}
      }
    }
  },
  computed: {
    user () {
      return this.$store.getters['user/getUser']
    }
  },
  components: {
    draggable,
    'fob-entity-select': EntitySelect,
    'fob-entity-select-prefetched': EntitySelectPrefetched,
    // 'fob-entity-table-select': EntityTableSelect,
    'fob-entity-multi-select': EntityMultiSelect,
    'fob-entity-multi-select-vertical': EntityMultiSelectVertical,
    'fob-projektleiter': Projektleiter,
    'fob-label': Label,
    'fob-html-editor': HTMLEditor
  },
  methods: {
    inEditView (field) {
      if (field.inEditView instanceof Function) {
        return field.inEditView(this.resourceData, this.user)
      } else {
        return field.inEditView
      }
    },
    hasAsterisk (field) {
      return this.$v.resourceData[field] != null &&
        this.$v.resourceData[field].$each == null &&
        (this.$v.resourceData[field].minLength != null ||
         this.$v.resourceData[field].required != null ||
         this.$v.resourceData[field].name != null ||
         (this.$v.resourceData[field].$params != null &&
          this.$v.resourceData[field].$params.required != null)
        )
    },
    emptyField (field) {
      let component = this.$refs[field]
      if (component) {
        /*
        Why is $refs[field] an array of length 1 here, while in contrast
        just a single component in the entityListOptions.refs?
        */
        component[0].emptyField()
      }
    },
    touchField (field) {
      if (this.$v.resourceData[field] != null) {
        this.$v.resourceData[field].$touch()
      }
    },
    isInvalid (field) {
      let componentInvalid = false
      let schema =
        this.schema.fields.find(fieldSchema => fieldSchema.field === field)
      if (schema.type === 'html-text') {
        componentInvalid = !this.$refs[field][0].componentValid
      }

      if (this.$v.resourceData[field] != null) {
        return componentInvalid || this.$v.resourceData[field].$invalid
      } else {
        return componentInvalid
      }
    },
    showModal (field) {
      const added = {}
      added[field] = true
      this.modalState = Object.assign({}, this.modalState, added)
    },
    pushToEmbedded (field, entityName) {
      if (!this.resourceData[field]) {
        this.$set(this.resourceData, field, [])
      }
      // provide fake ID
      let newEmbedded = new (EntityProvider(entityName))({ _id: `__${Date.now()}` })
      newEmbedded._entityName = entityName
      this.resourceData[field].push(newEmbedded)
    },
    removeEmbedded (field, idx) {
      this.$set(this.resourceData, field, this.resourceData[field].filter((x, i) => i !== idx))
    },
    // Function has to wait until watcher updates the data of the html-editor-component
    async isResourceDataInvalid () {
      await this.$nextTick()
      let fields = this.schema.fields.map(fieldSchema => fieldSchema.field)
      return fields.map(this.isInvalid).some(x => x)
    }
  },
  mounted () {
    // this is required so that the parent component gets the current state when
    // navigating to an partially filled in form
    this.isResourceDataInvalid().then(val => this.$emit('update:invalid', val))
  },
  watch: {
    resourceData: {
      handler (val) {
        this.$set(val, '_dirty', true)
        this.$emit('input', val)
        this.isResourceDataInvalid().then(val => this.$emit('update:invalid', val))
      },
      deep: true
    }
  }
}
</script>

<style lang="scss">
  .delay-enter-active {
    transition: all 0.5s;
  }

  .delay-leave-active {
    transition: all 0.5s;
    position: absolute;
  }
  .delay-leave-to  {
    transform: translateX(-50px);
    opacity: 0;
  }
  .delay-enter {
    transform: translateX(-50px);
    opacity: 0;
  }

  .formfields-item {
    transition: all 0.5s;
  }
  .formfields-enter, .formfields-leave-to {
    transition: all 0.5s;
    opacity: 0;
    transform: translateX(30px);
  }
  .formfields-move {
    transition: transform 0.5s;
  }
  .formfields-leave-active {
    position: absolute;
  }
  .formfields-enter-active, .formfields-leave-active {
    transition: all 1s;
  }

  .custom-date-picker {
    display: flex;
  }

  .custom-date-picker input::placeholder {
    color: black;
    opacity: 1;
  }

  .custom-date-picker-button {
    margin-left: 0.25rem;
  }

  /* hide label, but only for the person select */
  .projektleiter-person > div > .label {
    display: none;
  }

  /* prevent extra margin when create modal is open */
  .projektleiter-person > div > div {
    margin-bottom: 0 !important;
  }

  .projektleiter-person > div {
    width: 100%;
  }

  .projektleiter-person {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
  }

  .projektleiter-person > button {
    margin-left: 1rem;
  }
</style>



// WEBPACK FOOTER //
// src/components/form/EntityCreateForm.vue